import {Injectable} from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import {BehaviorSubject, firstValueFrom, map, Observable} from 'rxjs';
import {buildPath} from '@utils/util-builder';
import {
    CsfUploadResponse,
    HistoricalRepse,
    Provider,
    RepseConsultaResponse,
    RepseUploadResponse,
    ZipData
} from '@hotels/providers/contacts.types';
import {AccountRole} from '@enums/AccountRole';
import {environment} from '@env/environment';
import {ApiResponse} from '@models/api.response';
import {GenericSelect} from "@models/generic-select.interface";

/**
 * Creator: Fernando Tapia
 * Company: TAXSAT
 * Date: 08.02.22
 *
 * Copyright TAXSAT
 */
@Injectable({
    providedIn: 'root'
})
export class ProviderService {
    _apikey = environment.apiKeyEDOFAC;
    private readonly baseUrl: string = 'data-core/Company';
    private readonly _provider: BehaviorSubject<any> = new BehaviorSubject(null);

    constructor(private readonly _http: HttpClient) {
    }

    get provider$(): Observable<Provider> {
        return this._provider.asObservable();
    }

    set provider(value: Provider) {
        // Store the value
        this._provider.next(value);
    }

    /**
     * Method to get all providers
     */
    getAllProviders(): Observable<any> {
        return this._http.get<any>(buildPath(['data-core/Company/']));
    }

    /**
     * Check Repse status registry
     *
     * @param value
     */
    checkRepse(value: string): Promise<any> {
        return firstValueFrom(this._http.get<any>(buildPath(['data-core/Company/checkIfExistsRepse/' + value])));
    }

    /**
     * Consulta el padrón oficial REPSE por RFC libre (sin requerir que el RFC exista
     * como proveedor en SISE) — alta de proveedor del lado hotel (#140, backend #676).
     * 404 y errores de la fuente resuelven `null`: el alta nunca se bloquea por la
     * consulta, ambos casos habilitan la captura manual (AC-4).
     */
    async getPadronRepse(rfc: string): Promise<RepseConsultaResponse | null> {
        const url = buildPath(['data-core/repse/padron', encodeURIComponent(rfc)]);
        try {
            const response = await firstValueFrom(this._http.get<ApiResponse<RepseConsultaResponse>>(url));
            return response.data;
        } catch {
            return null;
        }
    }

    /**
     * Method to update status contract
     *
     * @param value
     * @param providerId
     */
    updateStatusContract(value: any, providerId: number): Observable<any> {
        return this._http.put<any>(buildPath(
            ['data-core/Company/changeStatus/' + value + '/proveedorId/' + providerId]), '');
    }

    /**
     * Method to get first upload information
     *
     * @param providerId
     */
    getFirstUpload(providerId: number): Observable<any> {
        return this._http.get<any>(buildPath(['data-core/FirstUpload/getBy/' + providerId]));
    }

    getHistoricalRepse(): Observable<HistoricalRepse[]> {
        return this._http.get<HistoricalRepse[]>(buildPath(['data-core/RepseHistory/getHistory']));
    }

    getHistoricalRepseByProvider(providerId: number): Observable<HistoricalRepse[]> {
        return this._http.get<HistoricalRepse[]>(buildPath(['data-core/RepseHistory/getHistoryByCompanyId/' + providerId]));
    }

    /**
     * Get provider by Id
     *
     * @param providerId
     */
    async getProviderByProveedor(providerId?: string): Promise<any> {
        const _user = JSON.parse(localStorage.getItem('user') ?? '');
        let provider;
        switch (_user.rol) {
            case AccountRole.master:
            case AccountRole.semiMaster:
            case AccountRole.adminHotel:
            case AccountRole.accountant:
                provider = await firstValueFrom(this._http.get<any>(buildPath(['data-core/Company/getProveedor/' + providerId])));
                break;
            case AccountRole.provider:
                provider = await firstValueFrom(this._http.get<any>(buildPath(['data-core/Company/getProveedor'])));
                break;
        }
        this._provider.next(provider);
        return provider;
    }

    /**
     * Method to create provider
     */
    async createProvider(providerId: number, body: any): Promise<any> {
        return await firstValueFrom(this._http.post<any>(buildPath(['data-core/Company/createCompany/proveedor/' + providerId]), body));
    }

    /**
     * Method to update provider
     */
    async updateProvider(body: any): Promise<any> {
        return await firstValueFrom(this._http.put<any>(buildPath(['data-core/Company/updateCompany']), body));
    }

    /**
     * Method to get Provider RFC
     */
    async getProviderByRFC(rfc: string): Promise<any> {
        return await firstValueFrom(this._http.get<any>(buildPath(['data-core/Company/getByRFC/' + rfc])));
    }

    /**
     * Method to get Provider RFC by Master
     */
    async getProviderByRFCMaster(rfc: string, hotelId: any): Promise<any> {
        return await firstValueFrom(this._http.get<any>(buildPath(['data-core/Company/Master/getByRFC/' + hotelId + '/' + rfc])));
    }

    /**
     *
     * @param contractImssRegistryId
     * @param period
     */
    getEvidenceZipData(contractImssRegistryId: number, period: string): Observable<ZipData> {
        const url = buildPath(['data-core/HotelCompanyZipController/get-by-contract-imss-registry-and-period', contractImssRegistryId.toString(), period])
        return this._http.get<ApiResponse<ZipData>>(url)
            .pipe(map((response: ApiResponse<ZipData>): any => response.data));
    }

    /**
     * Method to get Contract
     *
     * @param providerId
     */
    getContract(hotelId: number, providerId: any): Observable<any> {
        return this._http.get<any>(buildPath(['data-core/HotelCompany/getContratoBy/' + hotelId + '/' + providerId]));
    }

    /**
     * Method to get Contract History
     * @param hotelId
     * @param providerId
     */
    getContractHistory(hotelId: number, providerId: any): Observable<any> {
        return this._http.get<any>(buildPath(['data-core/ContractHistory/Master/getHistory/' + hotelId + '/' + providerId]));
    }

    /**
     * Get Contracts by Hotel
     * @param providerId
     */
    getContractHistoryByHotel(providerId: any): Observable<any> {
        return this._http.get<any>(buildPath(['data-core/ContractHistory/getHistoryByCompanyId/' + providerId]));
    }

    /**
     *
     * @param hotelId
     */
    getContractHistoryByCompany(hotelId: any): Observable<any> {
        return this._http.get<any>(buildPath(['data-core/ContractHistory/getHistoryByHotelId/' + hotelId]));
    }

    /**
     * Method to get account Id
     *
     * @param accountId
     */
    getAccount(accountId: any): Observable<any> {
        return this._http.get<any>(buildPath(['data-core/Account/' + accountId]));
    }

    /**
     * Method to get EDOFAC Status
     *
     * @param rfc
     */
    getEDOFAC(rfc: any): Observable<any> {
        const headers = new HttpHeaders({'x-api-key': this._apikey});
        const options = {headers: headers};
        return this._http.get<any>('https://backend.prod.morales.taxsat.com/providers/verifys-sis/' + rfc, options);
    }

    /**
     * Method to get result information KPIs EDOFAC
     * @param rfc
     */
    getEDOFACResults(rfc: any): Observable<any> {
        const headers = new HttpHeaders({'x-api-key': this._apikey});
        const options = {headers: headers};
        return this._http.get<any>('https://backend.prod.morales.taxsat.com/kpi/numbers/byRFC/' + rfc, options);
    }

    /**
     * Method to add provider information to hotel
     * @param rfcHotel
     * @param _name
     * @param _rfc
     */
    addProvider(rfcHotel: any, _name: string, _rfc: string): Observable<any> {
        const headers = new HttpHeaders({'x-api-key': this._apikey});
        const options = {headers: headers};
        return this._http.post<any>('https://backend.prod.morales.taxsat.com/providers/byRFC/' + rfcHotel, {
            name: _name,
            rfc: _rfc
        }, options);
    }

    /**
     * Method to add hotel information
     * @param _rfc
     * @param _name
     */
    addHotel(_rfc: string, _name: string): Observable<any> {
        const headers = new HttpHeaders({'x-api-key': this._apikey});
        const options = {headers: headers};
        return this._http.post<any>('https://backend.prod.morales.taxsat.com/company', {
            name: _name,
            rfc: _rfc
        }, options);
    }

    /**
     * Method to update repse Status
     */
    updateRepseStatus(status: boolean, proveedorId: any): Observable<any> {
        return this._http.put<any>(buildPath(['data-core/Company/changeRepseStatus/' + status + '/proveedorId/' + proveedorId]), '');
    }

    /**
     * Method to update sujetoEsquemaRepse status
     */
    changeSujetoEsquemaRepseStatus(status: boolean, proveedorId: any): Observable<any> {
        return this._http.put<any>(buildPath(['data-core/Company/changeSujetoEsquemaRepse/' + status + '/proveedorId/' + proveedorId]), '');
    }

    /**
     * Method to downloadfile
     *
     * @param nameFile
     */
    downloadFile(nameFile: string): Observable<any> {
        return this._http.get<any>(buildPath(['data-core/Company/getFileByName/' + nameFile]), {responseType: 'blob' as 'json'});
    }

    /**
     * Method to downloadHistoricalRepseFile
     *
     * @param nameFile
     */
    downloadHistoricalRepseFile(nameFile: string): Observable<any> {
        return this._http.get<any>(buildPath(['data-core/RepseHistory/getFileByName/' + nameFile]), {responseType: 'blob' as 'json'});
    }

    /**
     * Descarga en ZIP la información inicial disponible de un proveedor.
     *
     * @param providerId
     */
    downloadFirstUploadZip(providerId: number): Observable<any> {
        return this._http.get<any>(buildPath(['data-core/FirstUpload/' + providerId + '/zip']), {responseType: 'blob' as 'json'});
    }

    async getRepseHistory(companyId: number): Promise<RepseUploadResponse[]> {
        const url = buildPath(['data-core/FirstUpload/repse-history/' + companyId]);
        return firstValueFrom(this._http.get<RepseUploadResponse[]>(url));
    }

    async getCsfHistory(companyId: number): Promise<CsfUploadResponse[]> {
        const url = buildPath(['data-core/FirstUpload/csf-history/' + companyId]);
        return firstValueFrom(this._http.get<CsfUploadResponse[]>(url));
    }

    /**
     * Consulta el estatus REPSE oficial más reciente de un proveedor (SISE-platform#668).
     * Devuelve `null` cuando la fuente aún no tiene registro para ese RFC (404) — no es
     * un error, es la ausencia legítima de dato (alta inicial sin historial previo).
     *
     * @param rfc
     */
    async getRepseStatus(rfc: string): Promise<RepseConsultaResponse | null> {
        const url = buildPath(['data-core/repse', rfc]);
        try {
            const response = await firstValueFrom(this._http.get<ApiResponse<RepseConsultaResponse>>(url));
            return response.data;
        } catch (error: any) {
            if (error?.status === 404) {
                return null;
            }
            throw error;
        }
    }

    /**
     * Method to upload files
     *
     * @param formData
     */
    firstUpload(formData: any): Promise<any> {
        return firstValueFrom(this._http.post<any>(buildPath(['data-core/FirstUpload/firstUpload']), formData));
    }

    /**
     * Method to get providers by pattern
     *
     * @param pattern
     */
    async getProvidersByPattern(pattern: string): Promise<any> {
        const url: string = buildPath(['data-core/Company/findNamePattern', pattern]);
        return firstValueFrom(this._http.get<any>(url));
    }

    getContractorsNamesForSelector(): Observable<GenericSelect[]> {
        const url: string = buildPath([this.baseUrl, 'all-names']);
        return this._http.get<ApiResponse<GenericSelect[]>>(url).pipe(
            map((response: ApiResponse<GenericSelect[]>): GenericSelect[] => response.data)
        );
    }

    getContractorsNamesWithBailsForSelector(): Observable<GenericSelect[]> {
        const url: string = buildPath([this.baseUrl, 'all-names-fianza']);
        return this._http.get<ApiResponse<GenericSelect[]>>(url).pipe(
            map((response: ApiResponse<GenericSelect[]>): GenericSelect[] => response.data)
        );
    }

    /**
     * De una lista de proveedores, devuelve solo los que tienen al menos un documento de
     * información inicial disponible — usado para no mostrar en el selector masivo proveedores sin
     * nada que descargar. Ids fuera del alcance del usuario vienen omitidos, no causan error.
     *
     * @param providerIds ids de los proveedores a evaluar
     */
    getProveedoresConInformacion(providerIds: number[]): Observable<number[]> {
        const url = buildPath(['data-core/FirstUpload/con-informacion']);
        let params = new HttpParams();
        providerIds.forEach((id: number): void => {
            params = params.append('proveedorIds', id);
        });
        return this._http.get<ApiResponse<number[]>>(url, {params}).pipe(
            map((response: ApiResponse<number[]>): number[] => response.data)
        );
    }

    /**
     * Descarga en un solo ZIP la información inicial de varios proveedores, sin tope de cantidad.
     * Contrato real del backend: `GET /FirstUpload/zip` con `proveedorIds` repetido como query param
     * (ver `nk-sdd/wip/20260905-zip-info-masiva/2-technical/contracts/api.md` en SISE-platform).
     *
     * @param providerIds ids de los proveedores seleccionados
     */
    downloadZipMasivo(providerIds: number[]): Observable<HttpResponse<Blob>> {
        const url = buildPath(['data-core/FirstUpload/zip']);
        let params = new HttpParams();
        providerIds.forEach((id: number): void => {
            params = params.append('proveedorIds', id);
        });
        return this._http.get(url, {
            params,
            observe: 'response',
            responseType: 'blob'
        });
    }
}
